import React, {FC, useEffect, useMemo, useState} from 'react'
import {addDays} from 'date-fns'
import moment from 'moment'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import {
  getDashboardStatDataAsync,
  getReportFilterTypesAsync,
  getUserReportsAsync,
  getAccountSetupProgressAsync,
} from 'src/redux/actions/authActions'
import {GRAPH_DATA} from 'src/app/_interfaces/orderDetail.interface'
import OrderReportChartNew from './OrderReportChartNew'
import DashboardHero from './components/DashboardHero'
import DashboardSidebar from './components/DashboardSidebar'
import SalesByChannelCard from './components/SalesByChannelCard'
import TopStockedItemsCard from './components/TopStockedItemsCard'
import NewsUpdatesTab from './components/NewsUpdatesTab'
import NewsUpdatesSidebar from './components/NewsUpdatesSidebar'
import GetGoingTab from './components/GetGoingTab'
import {getAccountSetupProgress} from './utils/accountSetupProgress'
import {calcPercentChange, sumOrdersFromGraphData} from './utils/orderSummary'
import {DashboardStatData, DashboardTabId} from './types/dashboard.types'
import {
  DASHBOARD_PERIOD_OPTIONS,
  DashboardOrderDateFilter,
  DashboardPeriodOption,
  isDashboardPeriodFilter,
} from './utils/dashboardPeriodUtils'
import './dashboard.scss'

const tabs: {id: DashboardTabId; label: string}[] = [
  {id: 'dashboard', label: 'Dashboard'},
  {id: 'news', label: 'News & Updates'},
  {id: 'get-going', label: 'Get Going'},
]

const DashboardNew: FC = () => {
  const dispatch = useAppDispatch()
  const {fetchedConnectedPlatformData} = useAppSelector((state) => state.fetchConnectedPlatform)
  const {fetchedUserAccountData, fetchedUserAccountLoading} = useAppSelector(
    (state) => state.userAccountData
  )

  const [activeTab, setActiveTab] = useState<DashboardTabId>('dashboard')
  const [loading, setLoading] = useState(false)
  const [blueCardLoading, setBlueCardLoading] = useState(false)
  const [apiAccountProgress, setApiAccountProgress] = useState<{percent: number; remainingCount: number; fields?: Record<string, boolean>} | null>(null)
  const [apiAccountProgressLoading, setApiAccountProgressLoading] = useState(true)
  const [graphData, setGraphData] = useState<GRAPH_DATA[]>([])
  const [dashboardCurrency, setDashboardCurrency] = useState<string>('USD')
  const [dashboardCurrencySymbol, setDashboardCurrencySymbol] = useState<string>('')
  const [blueCardOrders, setBlueCardOrders] = useState<number>(0)
  const [blueCardPrevOrders, setBlueCardPrevOrders] = useState<number | null>(null)
  const [statDataLoading, setStatDataLoading] = useState(true)
  const [statData, setStatData] = useState<DashboardStatData>({
    orders_sync_errors: 0,
    synced_sku_count: 0,
    total_orders_synced: 0,
    total_sku_count: 0,
    pending_orders_count: 0,
    unlinked_items_count: 0,
    to_be_shipped_count: 0,
    inventory_mismatch_count: 0,
  })
  const [reportFilterOptions, setReportFilterOptions] =
    useState<DashboardPeriodOption[]>(DASHBOARD_PERIOD_OPTIONS)
  const [filters, setFilters] = useState<{
    from_date: string
    to_date: string
    platform_id: string
    filter_type: 'quantity' | 'value' | ''
    date_filter?: DashboardOrderDateFilter
  }>({
    from_date: moment(addDays(new Date(), -29)).utc(true).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
    to_date: moment(new Date()).utc(true).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
    platform_id: '',
    filter_type: 'value',
    date_filter: 'LAST_30_DAYS',
  })

  const accountProgress = useMemo(
    () => {
      if (apiAccountProgressLoading) {
        return {
          percent: 0,
          remainingCount: 0,
          milestones: [],
          isComplete: false,
          isLoading: true,
        }
      }
      if (apiAccountProgress) {
        return {
          percent: apiAccountProgress.percent,
          remainingCount: apiAccountProgress.remainingCount,
          milestones: [],
          fields: apiAccountProgress.fields,
          isComplete: apiAccountProgress.percent >= 100,
          isLoading: false,
        }
      }
      return {
        ...getAccountSetupProgress(fetchedUserAccountData, fetchedConnectedPlatformData || []),
        isLoading: false,
      }
    },
    [apiAccountProgress, apiAccountProgressLoading, fetchedUserAccountData, fetchedConnectedPlatformData]
  )

  const ordersTrendPercent = useMemo(() => {
    if (blueCardPrevOrders === null) return null
    return calcPercentChange(blueCardOrders, blueCardPrevOrders)
  }, [blueCardOrders, blueCardPrevOrders])

  useEffect(() => {
    if (fetchedUserAccountLoading || !fetchedUserAccountData?.id) return

    dispatch(getReportFilterTypesAsync()).then((res) => {
      const apiFilters: Array<{key: unknown; label: unknown}> = Array.isArray(
        res.payload?.data?.filters
      )
        ? res.payload.data.filters
        : []
      const validFilters = apiFilters.filter(
        (option): option is DashboardPeriodOption =>
          isDashboardPeriodFilter(option.key) && typeof option.label === 'string'
      )

      if (validFilters.length > 0) {
        setReportFilterOptions(validFilters)
      }
    })
  }, [dispatch, fetchedUserAccountData?.id, fetchedUserAccountLoading])

  useEffect(() => {
    if (fetchedUserAccountLoading || !fetchedUserAccountData?.id) return

    setBlueCardLoading(true)
    const currFrom = moment(addDays(new Date(), -29)).format('YYYY-MM-DD')
    const currTo = moment(new Date()).format('YYYY-MM-DD')
    const prevFrom = moment(addDays(new Date(), -59)).format('YYYY-MM-DD')
    const prevTo = moment(addDays(new Date(), -30)).format('YYYY-MM-DD')

    Promise.all([
      dispatch(
        getUserReportsAsync({
          user_id: fetchedUserAccountData.id,
          from_date: currFrom,
          to_date: currTo,
          platform_id: filters.platform_id,
          filter_type: 'quantity',
        })
      ),
      dispatch(
        getUserReportsAsync({
          user_id: fetchedUserAccountData.id,
          from_date: prevFrom,
          to_date: prevTo,
          platform_id: filters.platform_id,
          filter_type: 'quantity',
        })
      ),
    ])
      .then(([currRes, prevRes]) => {
        if (currRes.payload?.status_code === 200 && currRes.payload?.data?.graph_data) {
          setBlueCardOrders(sumOrdersFromGraphData(currRes.payload.data.graph_data))
        } else {
          setBlueCardOrders(0)
        }

        if (prevRes.payload?.status_code === 200 && prevRes.payload?.data?.graph_data) {
          setBlueCardPrevOrders(sumOrdersFromGraphData(prevRes.payload.data.graph_data))
        } else {
          setBlueCardPrevOrders(0)
        }
      })
      .catch(() => {
        setBlueCardOrders(0)
        setBlueCardPrevOrders(null)
      })
      .finally(() => {
        setBlueCardLoading(false)
      })
  }, [dispatch, fetchedUserAccountData?.id, fetchedUserAccountLoading, filters.platform_id])

  useEffect(() => {
    if (fetchedUserAccountLoading || !fetchedUserAccountData?.id) return

    setLoading(true)
    const usePresetDateFilter = filters.filter_type === 'value' && filters.date_filter
    dispatch(
      getUserReportsAsync({
        user_id: fetchedUserAccountData.id,
        platform_id: filters.platform_id,
        filter_type: filters.filter_type || 'quantity',
        ...(usePresetDateFilter
          ? {date_filter: filters.date_filter}
          : {
              from_date: moment(filters.from_date).format('YYYY-MM-DD'),
              to_date: moment(filters.to_date).format('YYYY-MM-DD'),
            }),
      })
    )
      .then((res) => {
        if (res.payload?.status_code === 200 && res.payload?.data) {
          setDashboardCurrency(res.payload.data.currency_iso || 'USD')
          setDashboardCurrencySymbol(res.payload.data.currency_symbol || '')
          if (res.payload.data.graph_data) {
            setGraphData(res.payload.data.graph_data)
          } else {
            setGraphData([])
          }
        } else {
          setGraphData([])
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [dispatch, fetchedUserAccountData?.id, fetchedUserAccountLoading, filters])

  useEffect(() => {
    if (fetchedUserAccountLoading || !fetchedUserAccountData?.id) return

    setStatDataLoading(true)
    dispatch(getDashboardStatDataAsync({user_id: fetchedUserAccountData.id}))
      .then((res) => {
        if (res.payload?.status_code === 200) {
          setStatData({
            orders_sync_errors: res.payload?.data?.orders_sync_errors ?? 0,
            synced_sku_count: res.payload?.data?.synced_sku_count ?? 0,
            total_orders_synced: res.payload?.data?.total_orders_synced ?? 0,
            total_sku_count: res.payload?.data?.total_sku_count ?? 0,
            pending_orders_count: res.payload?.data?.pending_orders_count ?? 0,
            unlinked_items_count: res.payload?.data?.unlinked_items_count ?? 0,
            to_be_shipped_count: res.payload?.data?.to_be_shipped_count ?? 0,
            inventory_mismatch_count: res.payload?.data?.inventory_mismatch_count ?? 0,
          })
        }
      })
      .catch(() => undefined)
      .finally(() => setStatDataLoading(false))
  }, [dispatch, fetchedUserAccountData?.id, fetchedUserAccountLoading])

  useEffect(() => {
    if (fetchedUserAccountLoading || !fetchedUserAccountData?.id) return

    dispatch(getAccountSetupProgressAsync({user_id: fetchedUserAccountData.id}))
      .then((res) => {
        if (res.payload?.status_code === 200 && res.payload?.data) {
          setApiAccountProgress({
            percent: res.payload.data.percentage ?? 0,
            remainingCount: res.payload.data.milestones_remaining ?? 0,
            fields: res.payload.data.fields,
          })
        }
      })
      .catch(() => undefined)
      .finally(() => setApiAccountProgressLoading(false))
  }, [dispatch, fetchedUserAccountData?.id, fetchedUserAccountLoading])

  return (
    <div className='page--dashboard-v2'>
      <DashboardHero
        user={fetchedUserAccountData}
        connectedPlatforms={fetchedConnectedPlatformData || []}
        accountProgress={accountProgress}
      />

      <div className='dashboard-tabs-bar'>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type='button'
            className={`dashboard-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' ? (
        <div className='dashboard-body'>
          <main>
            <OrderReportChartNew
              filters={filters}
              setFilters={setFilters}
              graphData={graphData}
              loading={loading}
              currencyIso={dashboardCurrency}
              currencySymbol={dashboardCurrencySymbol}
            />

            <div className='dashboard-grid-2'>
              <SalesByChannelCard filterOptions={reportFilterOptions} />
              <TopStockedItemsCard filterOptions={reportFilterOptions} />
            </div>
          </main>

          <DashboardSidebar
            statData={statData}
            statDataLoading={statDataLoading}
            platformId={filters.platform_id}
            connectedPlatforms={fetchedConnectedPlatformData || []}
            totalOrders={blueCardOrders}
            ordersTrendPercent={ordersTrendPercent}
            ordersSummaryLoading={blueCardLoading}
          />
        </div>
      ) : null}

      {activeTab === 'news' ? (
        <div className='dashboard-body'>
          <main>
            <NewsUpdatesTab />
          </main>
          <NewsUpdatesSidebar />
        </div>
      ) : null}

      {activeTab === 'get-going' ? (
        <div className='dashboard-body full-width'>
          <GetGoingTab />
        </div>
      ) : null}
    </div>
  )
}

export default DashboardNew
